import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Plus, Play, ArrowRight, Eye, Clock, Trash2, CheckCircle, XCircle, User, Users } from 'lucide-react';
import { toast } from 'sonner';
import { trTutar } from '@/lib/sayi';

const statusLabels = { 
  draft: 'Taslak', 
  in_progress: 'Devam Ediyor', 
  pending_warehouse: 'Depo Onayı Bekliyor',
  pending_approval: 'Yönetici Onayı Bekliyor',
  completed: 'Kesinleşti',
  rejected: 'Reddedildi'
};

const statusColors = {
  draft: 'bg-[#e8dfd0] text-[#3F3E39]',
  in_progress: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  pending_warehouse: 'bg-amber-100 text-amber-800',
  pending_approval: 'bg-[#2e2d29]/10 text-[#2e2d29]',
  completed: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  rejected: 'bg-[#7a3535]/10 text-[#7a3535]'
};

export default function StockCounts() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('stock_counts', 'delete');
  const [counts, setCounts] = useState([]);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedCount, setSelectedCount] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchCounts(); }, []);

  const fetchCounts = () => api.get('/stock/counts').then(r => setCounts(r.data)).catch(() => {});

  const startCount = async (id) => {
    try {
      await api.patch(`/stock/counts/${id}/start`);
      toast.success('Sayim baslatildi');
      navigate(`/erp/stock/counts/${id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const deleteCount = async (id) => {
    if (!window.confirm('Bu sayimi silmek istediginize emin misiniz?')) return;
    try {
      await api.delete(`/stock/counts/${id}`);
      toast.success('Sayim silindi');
      fetchCounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const handleApproveWarehouse = async (id) => {
    setActionLoading(true);
    try {
      await api.patch(`/stock/counts/${id}/approve-warehouse`);
      toast.success('Depo onayi verildi');
      fetchCounts();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Hata'); 
    }
    setActionLoading(false);
  };

  // YÖNETİCİ ONAYI LİSTEDEN VERİLMEZ: düğme doğrudan kesinleştiriyordu ve
  // detaydaki "sıfırlanacak ürünler listesi + onay kutusu" uyarısını tamamen
  // atlıyordu (2026-10-03). Onay detay sayfasında, uyarıyla birlikte.

  const handleReject = async () => {
    if (!selectedCount) return;
    if (rejectReason.trim().length < 3) { toast.error('Red sebebini yazın'); return; }
    setActionLoading(true);
    try {
      await api.patch(`/stock/counts/${selectedCount}/reject`, { reason: rejectReason });
      toast.success('Sayim reddedildi');
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedCount(null);
      fetchCounts();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Hata'); 
    }
    setActionLoading(false);
  };

  return (
    <div data-testid="stock-counts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Depo Sayimlari</h1>
          <p className="text-sm text-[#8a7e70] mt-1">{counts.length} sayim</p>
        </div>
        <Link to="/erp/stock/counts/new">
          <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-count-btn">
            <Plus size={16} className="mr-2" /> Yeni Sayim
          </Button>
        </Link>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Depo</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Urun Sayisi</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Stok Degeri</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Fark Tutari</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Olusturan</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Islemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {counts.map((c) => (
              <TableRow key={c.id} className={`erp-table-row border-[#d0c4b0] ${c.status === 'pending_warehouse' || c.status === 'pending_approval' ? 'bg-amber-50' : ''}`}>
                <TableCell className="text-sm text-[#3F3E39]">{new Date(c.count_date).toLocaleDateString('tr-TR')}</TableCell>
                <TableCell className="font-medium text-[#1a1916]">{c.warehouse_name}</TableCell>
                <TableCell>
                  <span className={`status-badge ${statusColors[c.status] || ''}`}>
                    {statusLabels[c.status] || c.status}
                  </span>
                </TableCell>
                <TableCell className="text-right text-[#3F3E39]">{c.urun_sayisi ?? 0}</TableCell>
                {/* Tutarlar sunucudan (`sayim_tutarlari`): sayılmayan kesinleşmede
                    SIFIRLANDIĞI için stok değeri yalnızca SAYILAN miktarlardan,
                    fark da sıfırlananlar dahil. Binlik ayraçlı, kuruşsuz. */}
                <TableCell className="text-right font-mono tabular-nums text-sm text-[#1a1916] whitespace-nowrap"
                  title={c.sistem_degeri != null ? `Sistemdeki değer: ${trTutar(c.sistem_degeri)} TL` : 'Sayılan ürünlerin değeri'}
                  data-testid={`sayim-deger-${c.id}`}>
                  {trTutar(c.sayim_degeri)} TL
                </TableCell>
                <TableCell className={`text-right font-mono tabular-nums font-semibold whitespace-nowrap ${(c.fark_tutari || 0) < 0 ? 'text-[#7a3535]' : 'text-[#3F3E39]'}`}
                  title={c.fark_tutari == null ? 'Sayım sürerken gizli — onay aşamasında görünür'
                    : c.sifirlanacak_sayisi > 0 ? `${c.sifirlanacak_sayisi} sayılmayan ürünün sıfırlanması dahil (${trTutar(c.sifirlanacak_degeri)} TL)` : undefined}
                  data-testid={`sayim-fark-${c.id}`}>
                  {c.fark_tutari == null ? <span className="text-[#b5aa9a] font-normal">—</span>
                    : `${c.fark_tutari > 0 ? '+' : ''}${trTutar(c.fark_tutari)} TL`}
                </TableCell>
                <TableCell className="text-sm text-[#8a7e70]">{c.created_by_name || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {c.status === 'draft' && (
                      <Button size="sm" variant="ghost" onClick={() => startCount(c.id)}
                        className="text-[#7a6a2e] hover:bg-[#7a6a2e]/10 text-xs h-7" data-testid={`start-count-${c.id}`}>
                        <Play size={14} className="mr-1" /> Baslat
                      </Button>
                    )}
                    {c.status === 'in_progress' && (
                      <Link to={`/erp/stock/counts/${c.id}`}>
                        <Button size="sm" variant="ghost"
                          className="text-[#7a6a2e] hover:bg-[#7a6a2e]/10 text-xs h-7" data-testid={`continue-count-${c.id}`}>
                          <ArrowRight size={14} className="mr-1" /> Devam Et
                        </Button>
                      </Link>
                    )}
                    {c.status === 'pending_warehouse' && (
                      <>
                        <Link to={`/erp/stock/counts/${c.id}`}>
                          <Button size="sm" variant="ghost" className="text-[#6b5f4f] hover:bg-[#e8dfd0] h-7"
                            title="İncele" data-testid={`view-wh-count-${c.id}`}>
                            <Eye size={14} />
                          </Button>
                        </Link>
                        <Button size="sm" variant="ghost" onClick={() => handleApproveWarehouse(c.id)}
                          disabled={actionLoading}
                          className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10 text-xs h-7" data-testid={`approve-wh-count-${c.id}`}>
                          <CheckCircle size={14} className="mr-1" /> Depo Onayla
                        </Button>
                        <Button size="sm" variant="ghost" 
                          onClick={() => { setSelectedCount(c.id); setShowRejectDialog(true); }}
                          className="text-[#7a3535] hover:bg-[#7a3535]/10 h-7" data-testid={`reject-count-${c.id}`}>
                          <XCircle size={14} />
                        </Button>
                      </>
                    )}
                    {c.status === 'pending_approval' && (
                      <>
                        <Link to={`/erp/stock/counts/${c.id}`}>
                          <Button size="sm" variant="ghost"
                            className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10 text-xs h-7" data-testid={`approve-mgr-count-${c.id}`}>
                            <CheckCircle size={14} className="mr-1" /> İncele ve Onayla
                          </Button>
                        </Link>
                        <Button size="sm" variant="ghost" 
                          onClick={() => { setSelectedCount(c.id); setShowRejectDialog(true); }}
                          className="text-[#7a3535] hover:bg-[#7a3535]/10 h-7" data-testid={`reject-mgr-count-${c.id}`}>
                          <XCircle size={14} />
                        </Button>
                      </>
                    )}
                    {c.status === 'completed' && (
                      <Link to={`/erp/stock/counts/${c.id}`}>
                        <Button size="sm" variant="ghost"
                          className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10 text-xs h-7" data-testid={`view-count-${c.id}`}>
                          <Eye size={14} className="mr-1" /> Goruntule
                        </Button>
                      </Link>
                    )}
                    {c.status !== 'completed' && c.status !== 'pending_warehouse' && c.status !== 'pending_approval' && silmeYetkisi && (
                      <Button size="icon" variant="ghost" onClick={() => deleteCount(c.id)}
                        className="h-8 w-8 text-[#8a7e70] hover:text-[#7a3535] hover:bg-[#7a3535]/10"
                        data-testid={`delete-count-${c.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {counts.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#8a7e70]">Sayim bulunamadi</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
              <XCircle size={18} className="text-[#7a3535]" /> Sayimi Reddet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Red Nedeni *</Label>
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Örn. Donuk ürünler sayılmamış, soğuk oda tekrar sayılsın"
                className="bg-white border-[#d0c4b0] min-h-[80px]" data-testid="red-sebebi" />
              <p className="text-xs text-[#8a7e70]">
                Sayım "Devam Ediyor"a döner, girilen miktarlar korunur. Sayımı yapan kişi
                bu notu sayım sayfasının başında görür.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectReason(''); setSelectedCount(null); }} 
              className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleReject} disabled={actionLoading || rejectReason.trim().length < 3}
              className="bg-[#7a3535] text-white hover:bg-[#5a2525]">
              {actionLoading ? 'Isleniyor...' : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
